import { Collapse, Stack, Typography } from '@mui/material';
import { ReactNode, useEffect } from 'react';
import { usePrevious } from 'react-use';

import { FormAwareButtonGroup } from '@/components/form/formAwareInputs/FormAwareButtonGroup';
import { FormAwareCheckbox } from '@/components/form/formAwareInputs/FormAwareCheckbox';
import { FormAwareCurrencyInput } from '@/components/form/formAwareInputs/FormAwareCurrencyInput';
import { FormAwarePercentInput } from '@/components/form/formAwareInputs/FormAwarePercentInput';
import { Card } from '@/components/layout/Card/Card';
import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import { PopperContent } from '@/components/poppers/PopperContent';
import { useFormContext } from '@/components/react-hook-form';
import {
  CombinedFormShape,
  CombinedPaths,
} from '@/modules/dispositiveProvisions/dispositiveProvisions.types';
import {
  DispositiveProvisionDispositionKind,
  TransferMethod,
} from '@/types/schema';

import { RecipientsBasePath } from './DispositiveProvisionsRecipient';

const TRANSFER_METHOD_OPTIONS = [
  { display: 'In kind', value: TransferMethod.InKind },
  { display: 'Liquidate', value: TransferMethod.LiquidateToCash },
];

/** The disposition kind values used by the button group in the form */
type DispositionKindSelection =
  | DispositiveProvisionDispositionKind.Percentage
  | DispositiveProvisionDispositionKind.Amount
  | 'other'
  | 'asset';

interface AdvancedSettingsCardProps {
  recipientsBasePath: RecipientsBasePath;
  idx: number;
  isExpanded: boolean;
  /** The currently selected disposition kind from the button group */
  dispositionKind: DispositionKindSelection | undefined;
  /** When true, shows the "Percentage of formula" input for formulaic distributions */
  showFormulaPercentage?: boolean;
  /** The selected formula disposition kind, used to determine if percentage should be disabled */
  selectedFormulaKind?: DispositiveProvisionDispositionKind | '';
  /** When true, hides the Transfer toggle. Used when only insurance policies are selected in asset DPs. */
  hideTransferToggle?: boolean;
  /** Additional content to render inside the advanced settings card */
  children?: ReactNode;
}

export function AdvancedSettingsCard({
  recipientsBasePath,
  idx,
  isExpanded,
  dispositionKind,
  showFormulaPercentage,
  selectedFormulaKind: _selectedFormulaKind,
  hideTransferToggle,
  children,
}: AdvancedSettingsCardProps) {
  const { control, setValue } = useFormContext<CombinedFormShape>();

  const isAsset = dispositionKind === 'asset';
  // For asset DPs, hide the transfer toggle when only insurance policies are selected.
  const effectiveShowTransferMethod = isAsset ? !hideTransferToggle : true;

  const showMaxDistributionLimit =
    !isAsset &&
    dispositionKind === DispositiveProvisionDispositionKind.Percentage;

  // Asset DPs don't support grow-by-inflation (inflation eligibility returns false for asset)
  const showGrowByInflation = dispositionKind != null && !isAsset;
  const isFormulaic = dispositionKind === 'other';
  const isDollar =
    dispositionKind === DispositiveProvisionDispositionKind.Amount;
  // Dollar distributions show only the Transfer ButtonGroup at full width.
  // Other types show it at fixed width alongside an input.
  const transferMinWidth = 180;

  // For formulaic dispositions, force growByInflation to true because
  // exemption values always adjust with inflation. When switching away from
  // formulaic, reset to false so the stale forced-on value doesn't persist.
  const prevIsFormulaic = usePrevious(isFormulaic);
  const growByInflationFieldName =
    `${recipientsBasePath}.${idx}.growByInflation` as const satisfies CombinedPaths;
  useEffect(() => {
    if (isFormulaic) {
      setValue(growByInflationFieldName, true);
    } else if (prevIsFormulaic) {
      setValue(growByInflationFieldName, false);
    }
  }, [isFormulaic, prevIsFormulaic, setValue, growByInflationFieldName]);

  return (
    <FormLayoutRow
      sx={{
        mb: isExpanded ? undefined : 0,
        '&:not(:last-child)': {
          mb: isExpanded ? undefined : 0,
        },
      }}
    >
      <FormLayoutItem width={12}>
        <Collapse in={isExpanded}>
          <Stack spacing={2}>
            <Card variant="filled-outline" sx={{ px: 2, py: 1.5 }}>
              <Stack spacing={1.5}>
                <Typography variant="title-xs">Advanced settings</Typography>
                {(isAsset ||
                  effectiveShowTransferMethod ||
                  showMaxDistributionLimit ||
                  showFormulaPercentage) && (
                  <Stack direction="row" spacing={1.5}>
                    {effectiveShowTransferMethod && (
                      <Stack
                        sx={
                          isDollar
                            ? { flex: 1 }
                            : { minWidth: transferMinWidth }
                        }
                      >
                        <FormAwareButtonGroup<CombinedFormShape>
                          label="Transfer"
                          fieldName={
                            `${recipientsBasePath}.${idx}.transferMethod` as const satisfies CombinedPaths
                          }
                          control={control}
                          options={TRANSFER_METHOD_OPTIONS}
                          contextualHelp={
                            <PopperContent body="In kind transfer assumes any characteristics, like discounts and growth rates, will carry through the death event; Liquidate assumes cash will be transferred and characteristics will not carry through the death event." />
                          }
                        />
                      </Stack>
                    )}
                    {isAsset && (
                      <Stack sx={{ flex: 1, minWidth: 0 }}>
                        <FormAwarePercentInput<CombinedFormShape>
                          label="Percentage of asset"
                          fieldName={
                            `${recipientsBasePath}.${idx}.dispositionAssetPercentage` as const satisfies CombinedPaths
                          }
                          control={control}
                          isDecimalJSInput
                          decimalScale={5}
                          fixedDecimalScale={false}
                          contextualHelp={
                            <PopperContent body="Specify what percentage of the selected assets to distribute to this recipient" />
                          }
                        />
                      </Stack>
                    )}
                    {showMaxDistributionLimit && (
                      <Stack sx={{ flex: 1, minWidth: 0 }}>
                        <FormAwareCurrencyInput<CombinedFormShape>
                          label="Max distribution amount"
                          fieldName={
                            `${recipientsBasePath}.${idx}.dispositionLimit` as const satisfies CombinedPaths
                          }
                          control={control}
                          isDecimalJSInput
                          contextualHelp={
                            <PopperContent body="Limit the disposition amount for this recipient to a specific dollar value" />
                          }
                        />
                      </Stack>
                    )}
                    {showFormulaPercentage && (
                      <Stack sx={{ flex: 1, minWidth: 0 }}>
                        <FormAwarePercentInput<CombinedFormShape>
                          label="Percentage of formula"
                          fieldName={
                            `${recipientsBasePath}.${idx}.dispositionFormulaicPercentage` as const satisfies CombinedPaths
                          }
                          control={control}
                          isDecimalJSInput
                          decimalScale={5}
                          fixedDecimalScale={false}
                          contextualHelp={
                            <PopperContent body="Specify what percentage of the formula to apply to this recipient" />
                          }
                        />
                      </Stack>
                    )}
                  </Stack>
                )}
                {showGrowByInflation && (
                  <FormAwareCheckbox<CombinedFormShape>
                    label="Grow by inflation"
                    fieldName={
                      `${recipientsBasePath}.${idx}.growByInflation` as const satisfies CombinedPaths
                    }
                    control={control}
                    disabled={isFormulaic}
                    helpText="Inflation rate set in the growth profile will be applied to this disposition"
                  />
                )}
                {children}
              </Stack>
            </Card>
          </Stack>
        </Collapse>
      </FormLayoutItem>
    </FormLayoutRow>
  );
}
